import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, LOGO_DARK } from '../config';
import ProfileSetupModal from './ProfileSetupModal';
import DisclaimerModal from './DisclaimerModal';
import Paywall from './Paywall';
import TinyWinsArchitect from './TinyWinsArchitect';
import BottomNavBar from './BottomNavBar';
import NotifyBanner from './NotifyBanner';
import MemberHome from './MemberHome';

// =============================================================================
// NOTES FROM THE JOURNEY - From "A Journey to Self-Discipline" by Pam Matambanadzo
// =============================================================================
const JOURNEY_NOTES = [
  "Self-discipline is not about being a taskmaster to yourself. It is about aligning your actions with your intentions.",
  "The goal is to overcome initial resistance with action, no matter how small.",
  "Small steps build momentum and make the task less intimidating.",
  "This journey is not about perfection... it is about progress.",
  "Start with small, actionable habits. Incremental progress builds momentum for greater change.",
  "Habits aren't just actions—they are a reflection of your identity.",
  "Self-discipline is the difference between dreaming and doing, between wishing and achieving.",
  "Align your habits with the person you want to become.",
  "Let curiosity and purpose guide you. Self-discipline is easier to sustain when aligned with a passion.",
  "Your 'why' gives you the strength to endure any 'how'.",
  "Set boundaries to protect your energy and focus on what truly matters.",
  "Focus on what you can control... and let go of what is outside of it.",
  "Growth isn't linear, and neither is the journey toward self-discipline.",
  "Self-discipline is not a destination. It's a practice that evolves as we do.",
  "Prioritize what matters most and say no to distractions.",
  "Delayed gratification isn't about deprivation; it's about investment.",
  "Patience may not come easily, but it is a skill that grows with practice.",
  "Discipline is not a single event but an ongoing journey.",
  "The sacrifices you make today can lead to monumental achievements tomorrow.",
  "Discipline grows stronger when tested.",
];

const getDailyNote = () => {
  const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  return JOURNEY_NOTES[dayOfYear % JOURNEY_NOTES.length];
};

const BOOK_AMAZON_URL = "https://www.amazon.com/Journey-Self-Discipline-Lessons-inspiration-potential/dp/B0DXPWJD7V";

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [access, setAccess] = useState(null);
  const [grouped, setGrouped] = useState({ active: [], graduated: [], paused: [] });
  const [fitnessGoal, setFitnessGoal] = useState(null);
  const [fitnessToday, setFitnessToday] = useState(null);
  const [activeGoalCount, setActiveGoalCount] = useState(0);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showArchitect, setShowArchitect] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [architectMode, setArchitectMode] = useState('normal');
  const [previousJourneyId, setPreviousJourneyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const checkAuth = async (architectIntent = null, prevId = null) => {
    try {
      const profileRes = await axios.get(`${API}/me`);
      setUser(profileRes.data);

      if (!profileRes.data.name || !profileRes.data.timezone) {
        setShowProfileSetup(true);
        setLoading(false);
        return;
      }
      if (!profileRes.data.termsAcceptedAt) {
        setShowDisclaimer(true);
        setLoading(false);
        return;
      }

      const accessRes = await axios.get(`${API}/access/check`);
      setAccess(accessRes.data);

      let g = { active: [], graduated: [], paused: [] };
      try {
        const gr = await axios.get(`${API}/journeys/grouped`);
        g = { active: gr.data.active || [], graduated: gr.data.graduated || [], paused: gr.data.paused || [] };
      } catch (e) { /* ignore */ }
      setGrouped(g);
      setActiveGoalCount(g.active.length);

      let hasFitness = false;
      try {
        const fr = await axios.get(`${API}/fitness`);
        if (fr.data.fitness_goal) {
          setFitnessGoal(fr.data.fitness_goal);
          setFitnessToday(fr.data.today);
          hasFitness = true;
        }
      } catch (e) { /* ignore */ }

      // Honor an architect intent handed over from a detail page (evolve / rescue)
      if (architectIntent) {
        setArchitectMode(architectIntent);
        if (prevId) setPreviousJourneyId(prevId);
        setShowArchitect(true);
        setLoading(false);
        return;
      }

      const hasAny = g.active.length + g.graduated.length + g.paused.length > 0;
      if (!hasAny && !hasFitness) {
        // Brand-new member: route through the assessment, then the Architect
        try {
          const sa = await axios.get(`${API}/assessment/smart`);
          if (!sa.data.smart_assessment) {
            navigate('/assessment', { replace: true });
            return;
          }
        } catch (e) {
          navigate('/assessment', { replace: true });
          return;
        }
        setArchitectMode('normal');
        setShowArchitect(true);
        setLoading(false);
        return;
      }

      // Returning member with history -> show the celebratory dashboard (never a day-one chat)
      setLoading(false);
    } catch (err) {
      navigate('/', { replace: true });
    }
  };

  useEffect(() => {
    const intent = location.state?.architect || null;
    const prevId = location.state?.previousJourneyId || null;
    if (intent) window.history.replaceState({}, document.title);
    (async () => { await checkAuth(intent, prevId); })();
  }, []);

  const handleProfileComplete = () => {
    setShowProfileSetup(false);
    checkAuth();
  };

  const handleDisclaimerAccepted = () => {
    setShowDisclaimer(false);
    setUser(prev => ({ ...prev, termsAcceptedAt: new Date().toISOString() }));
    checkAuth();
  };

  const handleJourneyComplete = () => {
    setShowArchitect(false);
    setArchitectMode('normal');
    setPreviousJourneyId(null);
    checkAuth();
  };

  const handleQuickLog = async (journey) => {
    try {
      await axios.post(`${API}/journeys/${journey.id}/log`, { completed: true });
      await checkAuth();
    } catch (e) {
      alert('Could not log today. Please try again.');
    }
  };

  const handleFitnessLog = async (completed) => {
    try {
      const res = await axios.post(`${API}/fitness/log`, { completed });
      setFitnessToday(prev => ({ ...prev, completed, logged: true }));
      setFitnessGoal(prev => ({ ...prev, current_streak: res.data.current_streak, longest_streak: res.data.longest_streak }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to log activity');
    }
  };

  const handleResume = async (id) => {
    try {
      await axios.post(`${API}/journeys/${id}/resume`);
      await checkAuth();
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not resume this journey.');
    }
  };

  const handleStartNewJourney = () => {
    const isPremium = user?.isPremium || access?.access === 'premium' || access?.access === 'trial' || access?.access === 'founder';
    if (activeGoalCount >= 3) {
      alert('You have reached the maximum of 3 active goals. Master or pause one to start a new one.');
      return;
    }
    if (!isPremium && activeGoalCount >= 1) {
      setShowUpgradeModal(true);
      return;
    }
    setArchitectMode(grouped.graduated.length > 0 ? 'returning' : 'normal');
    setShowArchitect(true);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) { /* ignore */ }
    navigate('/', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (showProfileSetup) {
    return <ProfileSetupModal user={user} onComplete={handleProfileComplete} />;
  }

  if (showDisclaimer) {
    return <DisclaimerModal onAgree={handleDisclaimerAccepted} />;
  }

  if (access?.access === 'paywall') {
    return <Paywall user={user} onLogout={handleLogout} />;
  }

  if (showArchitect) {
    return (
      <TinyWinsArchitect
        mode={architectMode}
        previousJourneyId={previousJourneyId}
        currentJourney={null}
        onComplete={handleJourneyComplete}
        onCancel={(grouped.active.length + grouped.graduated.length + grouped.paused.length > 0)
          ? () => { setShowArchitect(false); setArchitectMode('normal'); }
          : null}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <img src={LOGO_DARK} alt="Daily Discipline" className="h-10 w-10" />
              <span className="text-xl font-bold text-neutral-900">Daily Discipline™</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-neutral-600 hidden sm:block">Welcome, {user?.name || user?.email}</span>
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="text-neutral-500 hover:text-neutral-700 text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Access banners */}
      {access?.isGrandfathered && (
        <div className="bg-emerald-100 border-b border-emerald-200 py-2 text-center text-emerald-800 text-sm">
          ⭐ Founding Member - Lifetime Access
        </div>
      )}
      {access?.hasActiveGift && (
        <div className="bg-purple-100 border-b border-purple-200 py-2 text-center text-purple-800 text-sm">
          🎁 Gift access until {new Date(access.giftExpiresAt).toLocaleDateString()}
        </div>
      )}
      {access?.access === 'trial' && (
        <div className="bg-blue-100 border-b border-blue-200 py-2 text-center text-blue-800 text-sm">
          📅 Trial ends {new Date(access.trialEndsAt).toLocaleDateString()}
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <NotifyBanner />
        <MemberHome
          user={user}
          access={access}
          grouped={grouped}
          fitnessGoal={fitnessGoal}
          fitnessToday={fitnessToday}
          note={getDailyNote()}
          bookUrl={BOOK_AMAZON_URL}
          onOpenJourney={(id) => navigate(`/journey/${id}`)}
          onQuickLog={handleQuickLog}
          onFitnessLog={handleFitnessLog}
          onResume={handleResume}
          onStartNewJourney={handleStartNewJourney}
        />
      </main>

      <BottomNavBar currentPath="/dashboard" />

      {/* Upgrade to Premium Modal - free user attempting a 2nd goal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
            <div className="text-center mb-6">
              <span className="text-5xl">⭐</span>
              <h2 className="text-2xl font-bold text-neutral-900 mt-4">Unlock Multiple Goals</h2>
              <p className="text-neutral-600 mt-2">Free members focus on one habit at a time.</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
              <p className="text-purple-800 text-sm">
                <strong>Why one goal at a time?</strong><br />
                Research shows focusing on a single habit increases success. When you&apos;re ready for more, Premium unlocks up to 3 simultaneous goals.
              </p>
            </div>
            <button
              data-testid="upgrade-to-premium"
              onClick={() => { setShowUpgradeModal(false); navigate('/settings'); }}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-xl transition"
            >
              Upgrade to Premium
            </button>
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="w-full mt-3 text-neutral-500 hover:text-neutral-700 text-sm"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
